/**
 * A line item's targeting: locations (searched on X), languages, interests, platforms,
 * gender, age, keywords and custom audiences (include / exclude).
 * lib/x-ads/lineItemRules `toTargetingInput` turns it into exactly the body the backend reads.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { XCheck } from './XCheck';
import { XSelect } from './XSelect';
import { MultiPicker } from './MultiPicker';
import { ageLabel, label, num } from '@/lib/x-ads/format';
import type { Keyword, MatchType, Opt, TargetingState } from '@/lib/x-ads/lineItemRules';
import { xAdsService, type XAudience, type XConstants, type XTargetingOption } from '@/services/x-ads.service';

const toOpt = (o: XTargetingOption): Opt | null => (o.targetingValue ? {
  value: o.targetingValue,
  label: o.name || o.targetingValue,
  hint: [o.locationType && label(o.locationType), o.countryCode].filter(Boolean).join(' · ') || undefined,
} : null);
const toOpts = (list: XTargetingOption[]) => list.map(toOpt).filter((o): o is Opt => !!o);

export function TargetingForm({ uid, constants, value, onChange, disabled }: {
  uid: string; constants: XConstants | null; value: TargetingState; onChange: (next: TargetingState) => void; disabled?: boolean;
}) {
  const [languages, setLanguages] = useState<Opt[]>([]);
  const [interests, setInterests] = useState<Opt[]>([]);
  const [platforms, setPlatforms] = useState<Opt[]>([]);
  const [audiences, setAudiences] = useState<XAudience[] | null>(null);
  const [kw, setKw] = useState<Keyword>({ text: '', matchType: 'BROAD', exclude: false });
  const set = <K extends keyof TargetingState>(k: K, v: TargetingState[K]) => onChange({ ...value, [k]: v });

  useEffect(() => {
    if (!uid) return;
    let on = true;
    xAdsService.lookup(uid, 'languages').then((r) => on && setLanguages(toOpts(r))).catch(() => undefined);
    xAdsService.lookup(uid, 'interests').then((r) => on && setInterests(toOpts(r))).catch(() => undefined);
    xAdsService.lookup(uid, 'platforms').then((r) => on && setPlatforms(toOpts(r))).catch(() => undefined);
    xAdsService.audiences(uid).then((r) => on && setAudiences(r)).catch(() => on && setAudiences([]));
    return () => { on = false; };
  }, [uid]);

  // Memoised: the picker re-filters whenever its `options` array changes identity.
  const audienceOpts = useMemo<Opt[]>(() => (audiences ?? []).map((a) => ({
    value: a.id, label: a.name || a.id, hint: a.targetable === false ? 'Not targetable yet' : a.audienceSize ? `${num(a.audienceSize)} users` : undefined,
  })), [audiences]);
  const audiencePlaceholder = audiences?.length === 0 ? 'No custom audiences yet' : 'Search an audience';
  const searchLocations = useCallback(async (q: string) => toOpts(await xAdsService.lookup(uid, 'locations', q)), [uid]);
  const addKeyword = () => {
    const text = kw.text.trim();
    if (!text || text.length > 100 || value.keywords.some((k) => k.text === text && k.matchType === kw.matchType)) return;
    set('keywords', [...value.keywords, { ...kw, text }]);
    setKw({ ...kw, text: '' });
  };

  return <fieldset disabled={disabled} className="grid gap-x-6 md:grid-cols-2">
    <FormField label="Locations" htmlFor="xt-loc" hint="Countries, regions, cities…">
      <MultiPicker id="xt-loc" selected={value.locations} onChange={(v) => set('locations', v)} placeholder="Search a location" search={searchLocations} emptyMessage="X found no place with that name." disabled={disabled} />
    </FormField>
    <FormField label="Languages" htmlFor="xt-lang">
      <MultiPicker id="xt-lang" selected={value.languages} onChange={(v) => set('languages', v)} options={languages} placeholder="Search a language" disabled={disabled} />
    </FormField>
    <FormField label="Interests" htmlFor="xt-int">
      <MultiPicker id="xt-int" selected={value.interests} onChange={(v) => set('interests', v)} options={interests} placeholder="Search an interest" disabled={disabled} />
    </FormField>
    <FormField label="Platforms" hint="None ticked = all platforms">
      <div className="flex flex-wrap gap-2">
        {platforms.length === 0 && <span className="xa-muted">Loading…</span>}
        {platforms.map((p) => <label key={p.value} className={`xa-option !items-center !py-2 ${value.platforms.includes(p.value) ? 'is-on' : ''}`}>
          <XCheck checked={value.platforms.includes(p.value)} onChange={(on) => set('platforms', on ? [...value.platforms, p.value] : value.platforms.filter((x) => x !== p.value))} />
          {p.label}
        </label>)}
      </div>
    </FormField>
    <FormField label="Gender" htmlFor="xt-gender">
      <XSelect id="xt-gender" value={value.gender} onChange={(v) => set('gender', v as TargetingState['gender'])} clearable
        options={[{ value: '', label: 'Any' }, ...(constants?.genders ?? [{ value: '1', label: 'Male' }, { value: '2', label: 'Female' }])]} />
    </FormField>
    <FormField label="Age" htmlFor="xt-age" hint="One age range per line item">
      <XSelect id="xt-age" value={value.ageBucket} onChange={(v) => set('ageBucket', v)} clearable
        options={[{ value: '', label: 'Any age' }, ...(constants?.ageBuckets ?? []).map((a) => ({ value: a, label: ageLabel(a) }))]} />
    </FormField>
    <div className="md:col-span-2">
      <FormField label="Keywords" htmlFor="xt-kw" hint="Reach people who searched or posted these words">
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-[12rem] flex-1">
            <Input id="xt-kw" maxLength={100} placeholder="Type a keyword and press Enter" value={kw.text} onChange={(e) => setKw({ ...kw, text: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); } }} />
          </div>
          <XSelect className="w-40" value={kw.matchType} onChange={(v) => v && setKw({ ...kw, matchType: v as MatchType })}
            options={(['BROAD', 'PHRASE', 'EXACT', 'UNORDERED'] as MatchType[]).map((m) => ({ value: m, label: label(m) }))} />
          <label className={`xa-option h-10 !items-center !py-0 ${kw.exclude ? 'is-on' : ''}`}>
            <XCheck checked={kw.exclude} onChange={(c) => setKw({ ...kw, exclude: c })} />Exclude
          </label>
          <Button variant="secondary" onClick={addKeyword} disabled={!kw.text.trim()} iconLeft={<Plus size={14} />}>Add</Button>
        </div>
        {value.keywords.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5">
          {value.keywords.map((k, i) => <span key={`${k.text}-${k.matchType}`} className={`xa-chip ${k.exclude ? '!bg-danger-soft !text-danger' : ''}`}>
            {k.exclude ? 'Exclude: ' : ''}{k.text} <span className="opacity-70">({label(k.matchType)})</span>
            <button type="button" aria-label={`Remove keyword ${k.text}`} onClick={() => set('keywords', value.keywords.filter((_, j) => j !== i))}><X size={11} /></button>
          </span>)}
        </div>}
      </FormField>
    </div>
    <FormField label="Custom audiences — include" htmlFor="xt-aud">
      <MultiPicker id="xt-aud" selected={value.customAudiences} onChange={(v) => set('customAudiences', v)} options={audienceOpts} placeholder={audiencePlaceholder} emptyMessage="Create one under Tools → Audiences." disabled={disabled} />
    </FormField>
    <FormField label="Custom audiences — exclude" htmlFor="xt-aud-ex">
      <MultiPicker id="xt-aud-ex" selected={value.excludedCustomAudiences} onChange={(v) => set('excludedCustomAudiences', v)} options={audienceOpts} placeholder={audiencePlaceholder} emptyMessage="Create one under Tools → Audiences." disabled={disabled} />
    </FormField>
    <p className="xa-muted md:col-span-2">X needs at least 100 matched users before a custom audience can be targeted. Leave everything empty to reach everyone X allows.</p>
  </fieldset>;
}
